/** Timelines that run inside memory stages (scripted parts of otherwise interactive scenes). */
import type { TimelineData } from '@/core/types/contracts';
import { GAZE_TRIANGLE } from '../stages/defs';
import { artState } from '../actors';

/**
 * Where the island rite happens. The stage is 640×384 under a 640×360 view (camera scrollY ≤ 24) and the
 * dialogue box covers screen y ≥ 238, so the pentagon is laid on the open sand at world y ≤ 258: the lit
 * line stays visible while 「朝西南方」 is read.
 */
export const ISLAND_RITE = { x: 416, y: 236, radius: 40, glowIndex: 5 } as const;

/** Mark set by the stage's battle wrapper: `battle:<config>:<outcome>`. */
export const battleMark = (config: string, outcome: string): string => `battle:${config}:${outcome}`;

export const STAGE_TIMELINES: TimelineData[] = [
  // ---------------------------------------------------------------- storm
  {
    id: 'prologue-stage-storm-intro',
    skippable: true,
    steps: [
      { op: 'camera', x: 320, y: 200, ms: 0 },
      { op: 'wait', ms: 400 },
      { op: 'fx', effect: 'lightning', params: { shake: 6 }, wait: false },
      { op: 'sfx', key: 'sfx-thunder' },
      { op: 'wait', ms: 500 },
      { op: 'say', dialogue: 'prologue-storm-intro' },
    ],
  },
  {
    id: 'prologue-stage-storm-dawn',
    skippable: true,
    steps: [
      { op: 'anim', actor: 'jiang', state: 'idle' },
      { op: 'wait', ms: 600 },
      { op: 'fade', out: true, ms: 1200, color: '#c7ccd1' },
    ],
  },
  // ---------------------------------------------------------------- island
  {
    id: 'prologue-stage-island-landing',
    skippable: true,
    steps: [
      { op: 'camera', x: 320, y: 180, ms: 0 },
      { op: 'wait', ms: 600 },
      { op: 'face', actor: 'captain', facing: 'down' },
      { op: 'say', dialogue: 'prologue-island-landing' },
    ],
  },
  {
    id: 'prologue-stage-island-rite',
    skippable: true,
    steps: [
      { op: 'parallel', steps: [
        { op: 'move', actor: 'captain', x: 336, y: 206, speed: 60, wait: true },
        { op: 'move', actor: 'jiang', x: 368, y: 252, speed: 60, wait: true },
      ] },
      { op: 'face', actor: 'captain', facing: 'right' },
      { op: 'face', actor: 'jiang', facing: 'right' },
      { op: 'face', actor: 'lubaoding', facing: 'left' },
      { op: 'say', dialogue: 'prologue-island-rite' },
      { op: 'move', actor: 'lubaoding', x: ISLAND_RITE.x, y: 190, speed: 40, wait: true },
      { op: 'face', actor: 'lubaoding', facing: 'down' },
      { op: 'anim', actor: 'lubaoding', state: artState('rite') },
      { op: 'fx', effect: 'ritual-pentagon', params: { x: ISLAND_RITE.x, y: ISLAND_RITE.y, radius: ISLAND_RITE.radius, glowIndex: ISLAND_RITE.glowIndex }, wait: true },
      { op: 'sfx', key: 'sfx-ritual-glow' },
      { op: 'wait', ms: 900 }, // hold on the lit line before anyone speaks
      { op: 'anim', actor: 'lubaoding', state: artState('tired') },
      { op: 'say', dialogue: 'prologue-island-rite-result' },
      { op: 'anim', actor: 'captain', state: artState('bow') },
      { op: 'say', dialogue: 'prologue-island-rite-thanks' },
      { op: 'anim', actor: 'captain', state: 'idle' },
      { op: 'anim', actor: 'lubaoding', state: 'idle' },
      { op: 'parallel', steps: [
        { op: 'move', actor: 'captain', x: 240, y: 132, speed: 70, wait: true },
        { op: 'move', actor: 'sailor-1', x: 256, y: 140, speed: 70, wait: true },
        { op: 'move', actor: 'sailor-2', x: 224, y: 140, speed: 70, wait: true },
        { op: 'move', actor: 'jiang', x: 248, y: 152, speed: 70, wait: true },
      ] },
      { op: 'fade', out: true, ms: 800, color: '#c7ccd1' },
    ],
  },
  // ---------------------------------------------------------------- black water
  {
    id: 'prologue-stage-water-intro',
    skippable: true,
    steps: [
      { op: 'camera', x: 320, y: 200, ms: 0 },
      { op: 'wait', ms: 500 },
      { op: 'say', dialogue: 'prologue-water-intro' },
      { op: 'parallel', steps: [
        { op: 'move', actor: 'father', x: 88, y: 300, speed: 60, wait: true },
        { op: 'move', actor: 'mother', x: 104, y: 300, speed: 60, wait: true },
        { op: 'move', actor: 'qiong', x: 72, y: 300, speed: 60, wait: true },
      ] },
      { op: 'fx', effect: 'door-close', params: { x: 80, y: 316, actors: ['father', 'mother', 'qiong'] }, wait: true },
      { op: 'sfx', key: 'sfx-door-close' },
      { op: 'say', dialogue: 'prologue-water-door' },
      { op: 'move', actor: 'wanqianmei', x: 336, y: 228, speed: 40, wait: true },
      { op: 'face', actor: 'wanqianmei', facing: 'down' },
      { op: 'anim', actor: 'wanqianmei', state: artState('chant') },
      { op: 'fx', effect: 'ritual-triangle', params: { x: GAZE_TRIANGLE.x, y: GAZE_TRIANGLE.y }, wait: true },
      { op: 'sfx', key: 'sfx-ritual-triangle' },
      { op: 'anim', actor: 'wanqianmei', state: 'idle' },
      { op: 'say', dialogue: 'prologue-water-gaze' },
    ],
  },
  {
    id: 'prologue-stage-water-vision',
    skippable: true,
    steps: [
      { op: 'fx', effect: 'ritual-triangle-blackwater', params: { x: GAZE_TRIANGLE.x, y: GAZE_TRIANGLE.y }, wait: true },
      { op: 'sfx', key: 'sfx-black-water' },
      { op: 'anim', actor: 'jiang', state: 'emote', emote: 'eyes-closed' },
      { op: 'say', dialogue: 'prologue-water-vision' },
      { op: 'fade', out: true, ms: 900, color: '#000000' },
    ],
  },
  // ---------------------------------------------------------------- farewell
  {
    id: 'prologue-stage-farewell-intro',
    skippable: true,
    steps: [
      { op: 'camera', x: 320, y: 200, ms: 0 },
      { op: 'wait', ms: 500 },
      { op: 'say', dialogue: 'prologue-farewell-intro' },
    ],
  },
  {
    id: 'prologue-stage-farewell-father',
    skippable: true,
    steps: [
      { op: 'move', actor: 'father', x: 304, y: 212, speed: 50, wait: true },
      { op: 'face', actor: 'father', facing: 'down' },
      { op: 'face', actor: 'jiang', facing: 'up' },
      { op: 'anim', actor: 'father', state: artState('fold') },
      { op: 'say', dialogue: 'prologue-farewell-father' },
      { op: 'anim', actor: 'father', state: 'idle' },
      { op: 'emote', actor: 'qiong', emote: 'sad', ms: 1200 },
    ],
  },
  {
    id: 'prologue-stage-farewell-zhao',
    skippable: true,
    steps: [
      { op: 'fade', out: true, ms: 700, color: '#eef2f5' },
      { op: 'move', actor: 'jiang', x: 288, y: 268, speed: 400, wait: true },
      { op: 'face', actor: 'jiang', facing: 'right' },
      { op: 'move', actor: 'qiong', x: 256, y: 272, speed: 400, wait: true },
      { op: 'face', actor: 'qiong', facing: 'right' },
      { op: 'move', actor: 'father', x: 240, y: 240, speed: 400, wait: true },
      { op: 'face', actor: 'father', facing: 'right' },
      { op: 'fade', out: false, ms: 700, color: '#eef2f5' },
      { op: 'parallel', steps: [
        { op: 'say', dialogue: 'prologue-farewell-zhao-arrives' },
        { op: 'move', actor: 'zhao', x: 336, y: 268, speed: 48, wait: true },
      ] },
      { op: 'face', actor: 'zhao', facing: 'left' },
      { op: 'anim', actor: 'qiong', state: artState('cry') },
      { op: 'emote', actor: 'qiong', emote: 'cry', ms: 1500 },
      { op: 'say', dialogue: 'prologue-farewell-depart' },
      { op: 'anim', actor: 'qiong', state: artState('wave') },
      { op: 'parallel', steps: [
        { op: 'move', actor: 'zhao', x: 620, y: 268, speed: 56, wait: true },
        { op: 'move', actor: 'jiang', x: 600, y: 272, speed: 56, wait: true },
        { op: 'camera', x: 480, y: 240, ms: 4000 },
      ] },
      { op: 'fade', out: true, ms: 900, color: '#eef2f5' },
    ],
  },
  // ---------------------------------------------------------------- road
  {
    id: 'prologue-stage-road-talk',
    skippable: true,
    steps: [
      { op: 'camera', x: 320, y: 200, ms: 0 },
      { op: 'parallel', steps: [
        { op: 'move', actor: 'zhao', x: 352, y: 252, speed: 40, wait: true },
        { op: 'move', actor: 'jiang', x: 320, y: 260, speed: 40, wait: true },
        { op: 'camera', x: 336, y: 200, ms: 2500 },
      ] },
      { op: 'face', actor: 'zhao', facing: 'left' },
      { op: 'face', actor: 'jiang', facing: 'right' },
      { op: 'say', dialogue: 'prologue-road-talk' },
      { op: 'label', id: 'battle' },
      { op: 'battle', config: 'sword-training' },
      { op: 'goto', label: 'after-goal', ifMark: battleMark('sword-training', 'goal') },
      { op: 'say', dialogue: 'prologue-road-after-other' },
      { op: 'goto', label: 'after-done' },
      { op: 'label', id: 'after-goal' },
      { op: 'say', dialogue: 'prologue-road-after-goal' },
      { op: 'label', id: 'after-done' },
    ],
  },
  {
    id: 'prologue-stage-road-ship',
    skippable: true,
    steps: [
      { op: 'fade', out: true, ms: 600, color: '#e2c48c' },
      { op: 'fx', effect: 'ship-southwest', params: {}, wait: false },
      { op: 'fade', out: false, ms: 600, color: '#e2c48c' },
      { op: 'say', dialogue: 'prologue-road-ship' },
      { op: 'wait', ms: 600 },
      { op: 'fade', out: true, ms: 900, color: '#ffffff' },
    ],
  },
];
